// uiarc: filter-toolbar (https://uiarc.dev/r/filter-toolbar.json) yüzeyi ve "Tümünü temizle" düğmesi. Proje çubuğu
// filtre denetimlerini barındıran bir yerleşimdir; uiarc'ın chip satırı ve "Filtre ekle" menüsü (iki adımlı alan/değer
// seçimi) bu çubuğun alanlarının yerini tutmaz, bu yüzden alınmadı (docs/UIARC-PLAN.md §4 Faz 4).
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { Children, useState, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useIsMobile } from '@/hooks/use-mobile'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

interface FilterBarProps {
  children: ReactNode
  onReset?: () => void
  /** Mobilde katlanan (ilk denetimden sonraki) filtrelerden kaçı etkin; katlıyken sayı rozeti. */
  activeCount?: number
}

const textButtonClass = cn(
  'rounded-control text-text-muted leading-body min-h-(--control-height-sm) cursor-pointer border-0 bg-transparent px-3 text-sm tracking-(--tracking-body) whitespace-nowrap [-webkit-tap-highlight-color:transparent]',
  '[transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-spring)_var(--ease-spring)]',
  'hover:bg-surface-muted hover:text-foreground',
  'active:scale-[0.97] active:[transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-instant)_var(--ease-standard)]',
  'motion-reduce:transition-none motion-reduce:active:scale-100',
)

/**
 * Filtre kontrollerinin ortak yerleşimi. İş alanı bilgisi içermez (AGENTS.md §3);
 * hangi filtrelerin gösterileceğine `features/*` karar verir.
 *
 * Mobilde her denetim tam satır kapladığı için çubuk ekranın yarısını yiyip veriyi ilk ekranın
 * altına itiyordu: ilk denetim (çoğunlukla tarih, bağlam kaybolmasın) açık kalır, gerisi katlanır.
 */
export function FilterBar({ children, onReset, activeCount = 0 }: FilterBarProps) {
  const isMobile = useIsMobile()
  const [expanded, setExpanded] = useState(false)
  const controls = Children.toArray(children)
  const hidden = controls.length - 1
  const collapsible = isMobile && hidden > 0

  const reset = onReset && (
    <button type="button" onClick={onReset} className={cn(textButtonClass, 'ml-auto')}>
      {messages.filters.reset}
    </button>
  )

  return (
    <div
      data-slot="filter-bar"
      role="group"
      aria-label={messages.filters.title}
      className="border-border bg-surface rounded-panel flex flex-wrap items-end gap-x-4 gap-y-3 border px-4 py-3"
    >
      {collapsible && !expanded ? controls[0] : controls}
      {collapsible ? (
        <div className="flex basis-full items-center gap-2">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className={cn(
              textButtonClass,
              'text-foreground -ml-3 inline-flex items-center gap-1.5',
            )}
          >
            <SlidersHorizontal className="size-4" aria-hidden="true" />
            {expanded ? messages.filters.fewer : messages.filters.more(hidden)}
            {!expanded && activeCount > 0 && (
              <Badge tone="info" size="sm" className="tabular-nums">
                {messages.filters.activeCount(activeCount)}
              </Badge>
            )}
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'text-text-muted size-4 transition-transform duration-(--duration-fast) motion-reduce:transition-none',
                expanded && 'rotate-180',
              )}
            />
          </button>
          {reset}
        </div>
      ) : (
        reset
      )}
    </div>
  )
}

interface FilterFieldProps {
  label: string
  htmlFor?: string
  children: ReactNode
}

/** Etiket + kontrol çifti için ortak dikey yerleşim (uiarc alan etiketi, 7px boşluk). */
export function FilterField({ label, htmlFor, children }: FilterFieldProps) {
  return (
    <div className="flex flex-col gap-[7px]">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}
